import { ArrowPathIcon, CameraIcon, XCircleIcon, CheckIcon, PlusCircleIcon } from "@heroicons/react/24/solid";
import React, { useEffect, useRef, useState } from "react";
import { useLocalizedStrings } from "../Localization";
import { Button, Icon } from "@aidkitorg/component-library";

const left = "relative inline-flex items-center rounded-l-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10";
const right = "relative -ml-px inline-flex items-center rounded-r-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10";
const middle = "relative -ml-px inline-flex items-center bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10";

export default function Camera(props: {
  hidden?: boolean,
  enabled?: boolean,
  onSnapshot?(image: string): Promise<void>,
  onStateChange?(state: 'enabled' | 'disabled'): void,
  className?: string,
  cameraClassName?: string
}) {
  const cameraFeed = useRef<HTMLVideoElement>(null);
  const snapshot = useRef<HTMLCanvasElement>(null);
  const preview = useRef<HTMLImageElement>(null);
  const [pictureTaken, setPictureTaken] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const L = useLocalizedStrings();

  function updateCanvasSize() {
    if (!snapshot.current) return;

    if (snapshot.current?.width !== cameraFeed.current?.videoWidth || snapshot.current?.height !== cameraFeed.current?.videoHeight) {
      snapshot.current!.width = cameraFeed.current!.videoWidth;
      snapshot.current!.height = cameraFeed.current!.videoHeight;
    }
  }

  async function startCamera() {
    const media = await navigator.mediaDevices.getUserMedia({ video: true });
    cameraFeed.current!.srcObject = media;
    cameraFeed.current!.play();
    setPictureTaken(false);
    setShowCamera(true);
    props.onStateChange?.('enabled');
  }

  function takePicture() {
    const ctx = snapshot.current!.getContext('2d');
    const video = cameraFeed.current!;
    ctx?.drawImage(video, 0, 0, snapshot.current!.width, snapshot.current!.height);
    const image = snapshot.current!.toDataURL('image/png');
    preview.current!.src = image;
    setPictureTaken(true);
  }

  async function save() {
    cancel();
    await props.onSnapshot?.(preview.current!.src);
  }

  function cancel() {
    setShowCamera(false);
    setPictureTaken(false);
    props.onStateChange?.('disabled');
    (cameraFeed.current?.srcObject as MediaStream)?.getTracks()?.forEach(t => t.stop());
  }

  useEffect(() => {
    if (props.enabled) {
      startCamera();
    } else {
      cancel();
    }
  }, [props.enabled, snapshot, cameraFeed]);


  return (<div
    className={props.className}
    hidden={props.hidden}
  >
    <canvas ref={snapshot} hidden />
    <img ref={preview} hidden={!pictureTaken} className="pb-1.5" />
    <video
      ref={cameraFeed}
      className="pb-1.5"
      hidden={!showCamera || pictureTaken}
      onLoadedMetadata={updateCanvasSize} />
    <div className="flex flex-row space-x-2">
      <Button
        variant="secondary"
        icon={<Icon name="XCircleIcon" type="solid" />}
        hidden={!showCamera}
        onClick={cancel}
      >
        {L.apply.cancel}
      </Button>
      <Button 
        variant="secondary"
        icon={<Icon name="CameraIcon" type="solid" />}
        className={props.cameraClassName}
        hidden={showCamera}
        onClick={startCamera}
      >
        {L.selfie.take_photo}
      </Button>
      <Button
        variant="primary"
        icon={<Icon name="CameraIcon" type="solid" />}
        onClick={takePicture}
        hidden={!showCamera || pictureTaken}
        className="ml-auto"
      >
        {L.selfie.take_photo}
      </Button>
      {
        pictureTaken && (
          <div className="flex flex-row ml-auto space-x-2">
            <Button
              variant="secondary"
              icon={<Icon name="ArrowPathIcon" type="solid" />}
              onClick={() => setPictureTaken(false)}
            >
              {L.selfie.start_over}
            </Button>
            <Button
              variant="primary"
              icon={<Icon name="CheckIcon" type="solid" />}
              onClick={save}
            >
              {L.selfie.done}
            </Button>
          </div>
        )
      }
    </div>
  </div>);
}
